import {useOnClickOutside} from 'usehooks-ts'
import {FaCaretUp} from 'react-icons/fa6'
import {useTranslations} from 'use-intl'
import {useMemo, useRef} from 'react'
import clsx from 'clsx'
import {useBasketItemQuantityMutation, useBasketQuery} from '~/hooks/useBasket'
import BasketSkeletonLoader from '~/components/basket/BasketSkeletonLoader'
import {BasketItemList} from '~/components/basket/BasketItemList'
import {BasketError} from '~/components/basket/BasketErrorCard'
import {EmptyBasket} from '~/components/basket/EmptyBasketCard'
import BasketSummary from '~/components/basket/BasketSummary'
import {Button} from '~/components/common/Button'

type BasketOverlayProps = {
    className?: string
    onClose: () => void
}

const BasketOverlay = ({className, onClose}: BasketOverlayProps) => {
    const t = useTranslations('basket')
    const ref = useRef<HTMLDivElement>(null)
    const {data: basket, isLoading, isFetching, isError, error} = useBasketQuery()
    const {mutate: updateBasketItemQuantity, isPending} = useBasketItemQuantityMutation()

    const isBasketEmpty = !isLoading && !basket?.basketItems.length
    const isUpdatingBasket = isFetching || isPending

    useOnClickOutside(ref, (event) => {
        event.stopPropagation()
        onClose()
    })

    const checkoutLink = useMemo(() => {
        const baseHref = basket?.links.find((link) => link.type === 'shopCheckout')?.href

        if (!baseHref) {
            return basket?.links[0]?.href ?? undefined
        }

        const basketUrl = new URL(baseHref)
        basketUrl.searchParams.set('continueShoppingLink', window.location.href)

        return basketUrl.toString()
    }, [basket?.links])

    return (
        <div className={clsx(className)}>
            <FaCaretUp className="fill-[#0e0e16] size-10 -mb-5 ml-auto mr-2" />
            <div
                className="w-96 max-h-[75svh] bg-[#0e0e16] backdrop-blur-xl transform-gpu rounded-md shadow flex"
                ref={ref}
                aria-busy={isLoading}
                aria-live="polite"
            >
                <h2 className="sr-only">{t('title')}</h2>
                {isLoading && <BasketSkeletonLoader />}
                {!isLoading && (
                    <div className="p-4 flex flex-col overflow-hidden max-h-full">
                        {isError && <BasketError error={error} />}
                        {isBasketEmpty && <EmptyBasket />}

                        {!isBasketEmpty && basket && (
                            <>
                                <BasketItemList
                                    className="overflow-y-auto shrink"
                                    basketItems={basket.basketItems}
                                    onQuantityChange={updateBasketItemQuantity}
                                />
                                <div className="mt-2 border-t border-gray-600">
                                    <BasketSummary
                                        basket={basket}
                                        isRecalculating={isUpdatingBasket}
                                    />
                                    <Button className="mt-6" as="a" href={checkoutLink}>
                                        {t('checkout')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default BasketOverlay
